// ============================================================
// RenameClientDialog.js
// Modal on the Client detail page: rename the client.
// Submit → PATCH /api/mgmt/admin/clients/{id} { clientName }.
//
// Exists because, until 2026-09-25, there was no client edit path at
// all: a typo made in the trial provisioning dialog went out on every
// invitation email ("...for Schwarzwhife Ltd") and could only be fixed
// by a direct database write. The Tenant name is separate and has its
// own edit on the Tenant page (a tenant can serve several clients).
// ============================================================

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { renameAdminClient } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { toast } from '../components/Toaster';
import './CreateTenantDialog.css';

const MAX_CLIENT_NAME = 200;

export default function RenameClientDialog({ client, isOpen, onClose, onSaved }) {
  const [clientName, setClientName] = useState(client.clientName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  function handleClose() {
    if (isSubmitting) return;
    onClose();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const name = clientName.trim();
    if (!name) { setFieldError('Client name is required.'); return; }
    if (name.length > MAX_CLIENT_NAME) { setFieldError(`Client name must be ${MAX_CLIENT_NAME} characters or fewer.`); return; }
    setFieldError(null);
    if (name === client.clientName) { onClose(); return; }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const updated = await renameAdminClient(client.clientId, name);
      toast.success('Client renamed');
      onSaved(updated);
      onClose();
    } catch (err) {
      if (err && err.field) {
        setFieldError(err.detail || translateError(err));
      } else {
        setSubmitError(translateError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Rename client" closeDisabled={isSubmitting}>
      <form className="create-tenant-form" onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="create-tenant-form__banner" role="alert">{submitError}</div>
        )}

        <Field
          label="Client name"
          error={fieldError}
          required
          help="Shown to the client's users and used in invitation emails. The tenant name is edited separately on the Tenant page."
        >
          <input
            type="text"
            value={clientName}
            maxLength={MAX_CLIENT_NAME}
            disabled={isSubmitting}
            onChange={(e) => setClientName(e.target.value)}
            autoFocus
          />
        </Field>

        <div className="create-tenant-form__actions">
          <button
            type="button"
            className="create-tenant-form__cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="create-tenant-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={14} className="create-tenant-form__spinner" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
